import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  BaseForm,
  buildCustomFieldSelection,
  CustomFieldsSection,
  FormTag,
  SectionCard,
} from "components/BaseForm";
import CONFIG from "Config";
import { formatError } from "helpers/formatError";
import { useRevenueOpportunityMutations } from "hooks/useRevenueOpportunity";
import {
  SUCCESS_MESSAGES,
  TAG_MODEL_TYPE,
} from "routes/revenue_opportunity/form/constants";
import {
  buildDefaultValues,
  FORM_MODES,
} from "routes/revenue_opportunity/form/defaults";
import {
  removedAttachmentIds,
  toRevenueOpportunityPayload,
} from "routes/revenue_opportunity/form/payload";
import { buildRevenueOpportunitySchema } from "routes/revenue_opportunity/form/schema";
import AttachmentsSection from "routes/revenue_opportunity/form/sections/AttachmentsSection";
import DetailsSection from "routes/revenue_opportunity/form/sections/DetailsSection";
import InvoicesSection from "routes/revenue_opportunity/form/sections/invoices/InvoicesSection";
import OrdersSection from "routes/revenue_opportunity/form/sections/OrdersSection";
import PointsOfContactSection from "routes/revenue_opportunity/form/sections/PointsOfContactSection";
import RelationsSection, {
  buildRelationDisplays,
} from "routes/revenue_opportunity/form/sections/RelationsSection";
import StatusSection from "routes/revenue_opportunity/form/sections/StatusSection";
import { useAttachmentUploads } from "routes/revenue_opportunity/form/useAttachmentUploads";

const toDisplay = (id, label) => (id != null ? { id, label } : undefined);

/**
 * The Revenue Opportunity create, edit, and clone form. The page loads every
 * input to the defaults before mounting it, because BaseForm reads them once.
 */
const RevenueOpportunityForm = ({
  mode,
  record,
  preset,
  entityPrefill,
  regionCountries,
  presetRecords,
  customFieldConfigs,
  customFieldGroups = [],
  invoiceProducts,
  cloneTags,
  onSaved,
  onCancel,
}) => {
  const { create, update } = useRevenueOpportunityMutations();
  const attachmentUploads = useAttachmentUploads();
  const savedRef = useRef(null);
  const copiedAttachmentsRef = useRef(null);
  const isEdit = mode === FORM_MODES.edit;
  const isClone = mode === FORM_MODES.clone;

  const [customFieldSelection, setCustomFieldSelection] = useState(() =>
    buildCustomFieldSelection(
      customFieldConfigs,
      record ? (record.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(
    () =>
      buildRevenueOpportunitySchema({
        customFieldConfigs,
        disabledProductIds: invoiceProducts.disabledProductIds,
        isPiracyEnabled: CONFIG.ENABLE_PIRACY,
      }),
    [customFieldConfigs, invoiceProducts.disabledProductIds]
  );

  const defaultValues = useMemo(
    () =>
      buildDefaultValues({
        mode,
        record,
        preset,
        entityPrefill,
        regionCountries,
        customFieldConfigs,
        cloneTags,
      }),
    [
      mode,
      record,
      preset,
      entityPrefill,
      regionCountries,
      customFieldConfigs,
      cloneTags,
    ]
  );

  const relationDisplays = useMemo(
    () => buildRelationDisplays(record, presetRecords),
    [record, presetRecords]
  );
  const detailDisplays = {
    assignee: toDisplay(record?.assignee_id, record?.assignee_name),
    recipient: toDisplay(record?.recipient_id, record?.recipient_name),
  };
  const pointOfContactNames = Object.fromEntries(
    (record?.points_of_contact ?? []).map(poc => [
      poc.point_of_contact_id,
      poc.point_of_contact_name,
    ])
  );

  // The copies are made once, so a retried save does not upload them again.
  const copySourceAttachments = async sourceAttachments => {
    if (!copiedAttachmentsRef.current) {
      const { copied, failedNames } =
        await attachmentUploads.copyAttachments(sourceAttachments);
      if (failedNames.length) {
        toast.warning(`Could not copy: ${failedNames.join(", ")}`);
      }
      copiedAttachmentsRef.current = copied;
    }
    return copiedAttachmentsRef.current;
  };

  const handleSubmit = async values => {
    const copied =
      isClone && values.attachments.length
        ? await copySourceAttachments(values.attachments)
        : [];
    const data = toRevenueOpportunityPayload(values, {
      customFieldConfigs,
      attachments: [...copied, ...attachmentUploads.uploadedAttachments()],
    });
    try {
      savedRef.current = isEdit
        ? await update.mutateAsync({
            id: record.id,
            data,
            removedAttachmentIds: removedAttachmentIds(record, values),
          })
        : await create.mutateAsync(data);
    } catch (error) {
      // BaseForm shows the banner and field errors from the rethrown error.
      toast.error(formatError(error, "Could not save the Revenue Opportunity"));
      throw error;
    }
  };

  const handleSuccess = () => {
    toast.success(SUCCESS_MESSAGES[mode]);
    onSaved(savedRef.current);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
      shouldDisableSubmit={() => attachmentUploads.isUploading}
    >
      <div className="tw:flex tw:flex-col tw:gap-6">
        <RelationsSection
          mode={mode}
          origin={preset?.origin}
          recordCaseIds={record?.case_ids ?? []}
          displays={relationDisplays}
        />
        <DetailsSection displays={detailDisplays} />
        <StatusSection />
        <OrdersSection />
        <InvoicesSection
          productOptions={invoiceProducts.productOptions}
          disabledProductOptions={invoiceProducts.disabledProductOptions}
        />
        <PointsOfContactSection savedNames={pointOfContactNames} />
        <AttachmentsSection
          savedTitle={
            isClone ? "Copied from the source record" : "Saved attachments"
          }
          attachmentUploads={attachmentUploads}
        />
        <SectionCard title="Tags">
          <FormTag name="tags" label={null} modelType={TAG_MODEL_TYPE} />
        </SectionCard>
        {customFieldSelection.length > 0 && (
          <CustomFieldsSection
            selection={customFieldSelection}
            setSelection={setCustomFieldSelection}
            groups={customFieldGroups}
          />
        )}
      </div>
    </BaseForm>
  );
};

RevenueOpportunityForm.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  record: PropTypes.object,
  preset: PropTypes.shape({
    origin: PropTypes.string,
    entityId: PropTypes.number,
    contactId: PropTypes.number,
    campaignId: PropTypes.number,
    caseId: PropTypes.number,
  }),
  entityPrefill: PropTypes.shape({
    region_id: PropTypes.number,
    country: PropTypes.string,
  }),
  presetRecords: PropTypes.object,
  customFieldConfigs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  invoiceProducts: PropTypes.shape({
    productOptions: PropTypes.array.isRequired,
    disabledProductOptions: PropTypes.array.isRequired,
    disabledProductIds: PropTypes.instanceOf(Set).isRequired,
  }).isRequired,
  cloneTags: PropTypes.array,
  regionCountries: PropTypes.arrayOf(PropTypes.string),
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default RevenueOpportunityForm;
